import { inStage, isRetryableUpload, putToPresignedUrl, unwrap } from "@sagot/api-client";
import { contentTypeFor } from "@sagot/constants";
import type { IngestionStage } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useUploadErrorMessage } from "../../../lib/upload-errors";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "../shared/keys";
import { isKnowledgeStorageFull } from "../shared/storage-full";

export type UploadOutcome = "uploading" | "processing" | "unchanged" | "failed";

export interface UploadItem {
  key: string;
  name: string;
  sourceId: string | null;
  outcome: UploadOutcome;
  /** Byte progress while `uploading`. */
  progress: number;
  error?: string | undefined;
  /** A failed upload worth trying again (transport / storage / server error), not a rejected file. */
  retryable?: boolean | undefined;
  /** The knowledge storage meter is full: the row offers "Add storage" and "Compare plans" instead of a retry. */
  storageFull?: boolean | undefined;
}

export interface UploadMessages {
  storageFailed: string;
  infected: string;
}

/** What a queued upload needs to run again: the file and where the new source is scoped. */
interface UploadJob {
  file: File;
  audiences: readonly number[];
  agentIds: readonly string[];
}

let sequence = 0;

/**
 * Real file ingestion: presign → PUT with progress → finalize (DEC-014 presigned PUT + HEAD verify).
 * Every failure is an `UploadError` tagged with its stage; transport and server faults offer a retry,
 * validation, plan and virus verdicts do not.
 */
export function useUploads(messages: UploadMessages) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const errorMessage = useUploadErrorMessage();
  const [items, setItems] = useState<UploadItem[]>([]);
  const jobs = useRef(new Map<string, UploadJob>());

  const patch = useCallback((key: string, change: Partial<UploadItem>) => {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...change } : item)));
  }, []);

  const requestTicket = useCallback(
    (job: UploadJob, existingSourceId: string | null) => {
      const file = { filename: job.file.name, content_type: contentTypeFor(job.file), size_bytes: job.file.size, ocr_mode: "auto" as const };
      // A retry re-signs the same source (new version) instead of leaving an orphan behind.
      const body = existingSourceId
        ? { ...file, source_id: existingSourceId }
        : { ...file, audiences: [...job.audiences], ...(job.agentIds.length > 0 ? { agent_ids: [...job.agentIds] } : {}) };
      return inStage("presign", () => unwrap(api.POST("/v1/sources/uploads", { body })));
    },
    [api],
  );

  const uploadOne = useCallback(
    async (job: UploadJob, key: string, existingSourceId: string | null) => {
      try {
        const ticket = await requestTicket(job, existingSourceId);
        patch(key, { sourceId: ticket.source_id });
        void queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId), exact: true });
        const target = { url: ticket.upload_url, method: ticket.method, headers: ticket.headers };
        await putToPresignedUrl(target, job.file, { onProgress: (progress) => { patch(key, { progress }); } });
        const result = await inStage("finalize", () => unwrap(api.POST("/v1/sources/{source_id}/finalize", { params: { path: { source_id: ticket.source_id } } })));
        if (result.outcome === "infected") patch(key, { outcome: "failed", retryable: false, error: result.reason ?? messages.infected });
        else patch(key, { outcome: result.outcome === "unchanged" ? "unchanged" : "processing" });
      } catch (error) {
        patch(key, { outcome: "failed", retryable: isRetryableUpload(error), storageFull: isKnowledgeStorageFull(error), error: errorMessage(error, messages.storageFailed) });
      } finally {
        void queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId), exact: true });
      }
    },
    [api, errorMessage, messages, patch, queryClient, requestTicket, wsId],
  );

  const start = useCallback(
    (files: readonly File[], audiences: readonly number[], agentIds: readonly string[] = []) => {
      const created = files.map((file) => {
        sequence += 1;
        const item: UploadItem = { key: `upload-${String(sequence)}`, name: file.name, sourceId: null, outcome: "uploading", progress: 0 };
        const job: UploadJob = { file, audiences, agentIds };
        jobs.current.set(item.key, job);
        return { job, item };
      });
      setItems((current) => [...current, ...created.map((c) => c.item)]);
      return Promise.all(created.map(({ job, item }) => uploadOne(job, item.key, null)));
    },
    [uploadOne],
  );

  const retry = useCallback(
    (key: string) => {
      const job = jobs.current.get(key);
      const item = items.find((candidate) => candidate.key === key);
      if (!job || item?.outcome !== "failed" || item.retryable !== true) return Promise.resolve();
      patch(key, { outcome: "uploading", progress: 0, error: undefined, retryable: undefined, storageFull: undefined });
      return uploadOne(job, key, item.sourceId);
    },
    [items, patch, uploadOne],
  );

  const clearFinished = useCallback(() => {
    setItems((current) => current.filter((item) => item.outcome === "uploading"));
  }, []);

  const busy = items.some((item) => item.outcome === "uploading");
  return { items, start, retry, clearFinished, busy };
}

/** Stage shown for an upload row: byte upload first, then the source's live pipeline stage. */
export function uploadStage(item: UploadItem, liveStage: IngestionStage | undefined): IngestionStage {
  if (item.outcome === "failed") return "failed";
  if (item.outcome === "uploading") return "uploading";
  if (item.outcome === "unchanged") return "ready";
  return liveStage ?? "parsing";
}
